/**
 * Recipe parameters (mixer PLC setpoints).
 *
 * Mirrors RECIPE_PARAMS in nexus/modules/devices/plcEscherRecipeWriter.py and
 * RECIPE_PARAMETERS in hive/src/Core/MES/mesRecipeUtils.ts 1:1. `key` is what
 * travels to the writer script, so renaming one here alone silently writes the
 * wrong register — change all three together.
 *
 * There are deliberately no default values: every product is mixed differently,
 * so a seeded number would read as a setpoint somebody had chosen for that
 * recipe. Unset parameters are empty until entered.
 *
 * Two labels because the two screens that use this speak different languages:
 * MES is Croatian, the Devices module is English.
 */
export interface RecipeParameterDef {
  key: string;
  label: string;
  labelHr: string;
  unit: string;
}

export const RECIPE_PARAMETERS: RecipeParameterDef[] = [
  { key: 'water1', label: 'Water 1', labelHr: 'Voda 1', unit: 'l' },
  { key: 'water2', label: 'Water 2', labelHr: 'Voda 2', unit: 'l' },
  { key: 'salt', label: 'Salt', labelHr: 'Sol', unit: 'kg' },
  { key: 'oil', label: 'Oil', labelHr: 'Ulje', unit: 'l' },
  { key: 'starter', label: 'Starter', labelHr: 'Starter', unit: 'kg' },
  { key: 'devWater2StartEnergy', label: 'Development water 2 start energy', labelHr: 'Energija početka vode 2 u razvoju', unit: '' },
  { key: 'devTotalEnergy', label: 'Development total energy', labelHr: 'Ukupna energija razvoja', unit: '' },
  { key: 'offsetStartSalt', label: 'Offset to start salt', labelHr: 'Odmak do dodavanja soli', unit: '' },
  { key: 'water1Temp', label: 'Water 1 temperature', labelHr: 'Temperatura vode 1', unit: '°C' },
  { key: 'controlSelection', label: 'Control selection', labelHr: 'Odabir regulacije', unit: '' },
  { key: 'water2DosingTime', label: 'Water 2 dosing time', labelHr: 'Vrijeme doziranja vode 2', unit: 's' },
  { key: 'oilDosingTime', label: 'Oil dosing time', labelHr: 'Vrijeme doziranja ulja', unit: 's' },
  { key: 'homogenisationTime', label: 'Homogenisation time', labelHr: 'Vrijeme homogenizacije', unit: 's' },
  { key: 'devWater2StartTime', label: 'Development water 2 start time', labelHr: 'Vrijeme početka vode 2 u razvoju', unit: 's' },
  { key: 'devTotalTime', label: 'Development total time', labelHr: 'Ukupno vrijeme razvoja', unit: 's' },
  { key: 'offsetStartOil', label: 'Offset to start oil', labelHr: 'Odmak do dodavanja ulja', unit: 's' },
];

/**
 * Every parameter as an empty string, for plain `<input type="number">` fields
 * that have to keep a half-typed "0." intact across renders. Forms built on
 * NumberInput get that for free and should use the nullable numeric map instead.
 */
export const emptyRecipeParameterText = (): Record<string, string> =>
  RECIPE_PARAMETERS.reduce(
    (acc, def) => ({ ...acc, [def.key]: '' }),
    {} as Record<string, string>
  );

export const emptyRecipeParameterValues = (): Record<string, number | null> =>
  RECIPE_PARAMETERS.reduce(
    (acc, def) => ({ ...acc, [def.key]: null }),
    {} as Record<string, number | null>
  );

/** Keys still without a value — the PLC must not be written a partial recipe. */
export const missingRecipeParameters = (values: Record<string, unknown>): RecipeParameterDef[] =>
  RECIPE_PARAMETERS.filter((def) => {
    const value = values[def.key];
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '' || !Number.isFinite(Number(value));
    return !Number.isFinite(Number(value));
  });
